/**
 * @fileoverview PWA install button component.
 * @summary Button to prompt users to install the PWA.
 *
 * @package KaraoNow
 * @module apps/front/src/components/PWAInstallButton
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { usePWA } from '../hooks/usePWA.js'

interface PWAInstallButtonProps {
  /** Additional CSS classes */
  className?: string
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost'
  /** Show text label */
  showLabel?: boolean
}

/**
 * @summary PWA install button component.
 * @description Shows a button to install the PWA when available.
 * @param {PWAInstallButtonProps} props - Component props.
 * @returns {JSX.Element | null} Install button or null if not available.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function PWAInstallButton({
  className = '',
  variant = 'primary',
  showLabel = true,
}: PWAInstallButtonProps): React.ReactElement | null {
  const { t } = useTranslation()
  const { canInstall, installPrompt } = usePWA()

  if (!canInstall) {
    return null
  }

  const handleInstall = async () => {
    await installPrompt()
  }

  const baseClasses = 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors'
  const variantClasses = {
    primary: 'bg-(--color-primary) text-white hover:bg-(--color-primary-dark)',
    secondary: 'bg-(--color-bg-secondary) text-(--color-text-primary) hover:bg-(--color-bg-tertiary) border border-(--color-border)',
    ghost: 'text-(--color-text-primary) hover:bg-(--color-bg-secondary)',
  }

  return (
    <button
      onClick={handleInstall}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      title={t('pwa.installApp')}
    >
      <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
      {showLabel && <span>{t('pwa.install')}</span>}
    </button>
  )
}

export default PWAInstallButton

